import React, {useState} from 'react';
import createAxiosInstance from '../../../support/AxiosSupport';
import {MapPinIcon} from '../../icons';
import {StepProps, asRecord, stringField} from './types';

/**
 * Шаг «Город и погода» (слот 10).
 *
 * Город — свободный текст, он уезжает в `UserSettings.location` при завершении мастера.
 * Погода — отдельная история: она требует координат и явного согласия, контракт тот же, что
 * у `WeatherLocationForm` (`PUT /api/weather/location`), поэтому подключение погоды шаг
 * выполняет сам и отмечает результат в ответе (`weatherEnabled`).
 */
const StepLocation: React.FC<StepProps> = ({value, onChange}) => {
    const current = asRecord(value);
    const city = stringField(value, 'city');
    const weatherEnabled = current.weatherEnabled === true;

    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [consent, setConsent] = useState(false);
    const [geoBusy, setGeoBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const latNum = Number(lat);
    const lngNum = Number(lng);
    const coordsValid = lat.trim() !== '' && lng.trim() !== ''
        && !Number.isNaN(latNum) && !Number.isNaN(lngNum)
        && latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180;

    const detectLocation = () => {
        setError(null);
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setError('Геолокация недоступна — введите координаты вручную');
            return;
        }
        setGeoBusy(true);
        navigator.geolocation.getCurrentPosition(
            pos => {
                setLat(pos.coords.latitude.toFixed(4));
                setLng(pos.coords.longitude.toFixed(4));
                setGeoBusy(false);
            },
            () => {
                setError('Не удалось определить местоположение — введите координаты вручную');
                setGeoBusy(false);
            },
        );
    };

    const enableWeather = async () => {
        if (!coordsValid || !consent || saving) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            await api.put('/weather/location', {
                city: city.trim() || undefined,
                latitude: latNum,
                longitude: lngNum,
                consentGranted: true,
            });
            onChange({...current, city, weatherEnabled: true});
        } catch {
            setError('Не удалось сохранить локацию. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="onboarding-step-location">
            <label className="flex flex-col gap-1">
                <span className="text-sm text-gray-700 dark:text-gray-300">Город</span>
                <input
                    type="text"
                    maxLength={255}
                    value={city}
                    onChange={e => onChange({...current, city: e.target.value})}
                    className="input-field min-h-[44px]"
                    placeholder="Москва"
                    data-testid="onboarding-location-city"
                />
            </label>

            {weatherEnabled ? (
                <p className="text-sm text-emerald-600 dark:text-emerald-400" data-testid="onboarding-weather-enabled">
                    Погода подключена — образ дня будет учитывать прогноз.
                </p>
            ) : (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-ink-700 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <MapPinIcon className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5"/>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Чтобы образ дня учитывал прогноз, нужны координаты и согласие. Локация
                            хранится огрублённо, шаг не обязателен.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="flex flex-col gap-1">
                            <span className="text-sm text-gray-700 dark:text-gray-300">Широта</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={lat}
                                onChange={e => setLat(e.target.value)}
                                className="input-field min-h-[44px]"
                                placeholder="55.7558"
                                data-testid="onboarding-location-lat"
                            />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-sm text-gray-700 dark:text-gray-300">Долгота</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={lng}
                                onChange={e => setLng(e.target.value)}
                                className="input-field min-h-[44px]"
                                placeholder="37.6173"
                                data-testid="onboarding-location-lng"
                            />
                        </label>
                    </div>
                    <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={e => setConsent(e.target.checked)}
                            className="mt-1 accent-brand-600"
                            data-testid="onboarding-location-consent"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Согласен на обработку приблизительной геопозиции для прогноза погоды
                        </span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={detectLocation}
                            disabled={geoBusy}
                            className="btn-secondary min-h-[44px]"
                            data-testid="onboarding-location-detect"
                        >
                            {geoBusy ? 'Определяем…' : 'Определить автоматически'}
                        </button>
                        <button
                            type="button"
                            onClick={enableWeather}
                            disabled={!coordsValid || !consent || saving}
                            className="btn-secondary min-h-[44px]"
                            data-testid="onboarding-location-save"
                        >
                            {saving ? 'Сохранение…' : 'Включить погоду'}
                        </button>
                    </div>
                    {error && (
                        <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="onboarding-location-error">
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default StepLocation;
